import { deepEquals } from '@beesoft/common';
import type { Key } from 'react';
import type { JsonData, JsonDataItem } from '../generic-types';
import { DataGridColumnType } from './data-grid-enums';
import type { DataGridColumnSettings } from './data-grid-settings.interface';

const DEFAULT_MIN_COLUMN_WIDTH_PX = 142;
const DEFAULT_COLUMN_WIDTH_PX = 150;
const DEFAULT_MAX_COLUMN_WIDTH_PX = 350;
const DEFAULT_MIN_TEXT_COLUMN_WIDTH_PX = 120; // Reduced from 400px to allow short text to size appropriately
const DEFAULT_MAX_TEXT_COLUMN_WIDTH_PX = 450;
export const ABSOLUTE_MIN_COLUMN_WIDTH_PX = 70; // Minimum width to prevent layout breakage during manual resize
export const CELL_RIGHT_MARGIN_PX = 12; // mr-3
export const CELL_PADDING_LEFT_PX = 8; // pl-2
export const STATUS_INDICATOR_CELL_WIDTH_PX = 40; // w-10

export function getIdFieldColumn(columns: Array<DataGridColumnSettings>) {
  for (let i = 0, length = columns.length; i < length; i++) {
    if (columns[i].isIdField) {
      return columns[i];
    }
  }
}

export function hasColumnChanged(
  forceResetColumnMap: Map<Key, boolean>,
  column: DataGridColumnSettings,
  previousColumn?: DataGridColumnSettings
) {
  const isForceReset = forceResetColumnMap.has(column.id) && forceResetColumnMap.get(column.id) === true;

  forceResetColumnMap.delete(column.id);

  return (
    isForceReset ||
    !previousColumn ||
    previousColumn.field !== column.field ||
    previousColumn.id !== column.id ||
    !deepEquals(column.searchValue, previousColumn.searchValue)
  );
}

export function setColumnDefaultValues(columns: Array<DataGridColumnSettings>, autoSizeColumns?: boolean) {
  for (let i = 0, length = columns.length; i < length; i++) {
    const column = columns[i];

    column.minWidth = getColumnMinWidth(column);
    column.width = column.width ?? (autoSizeColumns ? getColumnMinWidth(column) : getColumnDefaultWidth(column));
    column.maxWidth = getColumnMaxWidth(column);
    column.type = column.type ?? DataGridColumnType.Custom;
  }

  return columns;
}

// Retruns the sum of all visible columns
export function calculateRowWidth(columns: Array<DataGridColumnSettings>) {
  let rowWidth = 0;
  const renderColumns = columns.filter((column) => column.visible);
  for (let i = 0, length = renderColumns.length; i < length; i++) {
    const actualColumnWidth = renderColumns[i].width || getColumnDefaultWidth(renderColumns[i]);
    rowWidth += actualColumnWidth + CELL_RIGHT_MARGIN_PX;
  }

  return rowWidth;
}

// Updates the visible columns widths to fill up the available space
export function adjustColumnsToTakeAvailableSpace(totalWidth: number, columns: Array<DataGridColumnSettings>) {
  // Floor totalWidth to prevent fractional residuals from getBoundingClientRect()
  // which can cause columns to exceed the integer clientWidth of the scroll container.
  totalWidth = Math.floor(totalWidth);

  // calculate the remaining width
  const rowWidth = calculateRowWidth(columns);
  const remainingWidth = totalWidth - rowWidth;
  //Check if available space has shrunk or grown
  if (Math.abs(remainingWidth) < 1) {
    return columns;
  }

  const adjustableColumns = columns.filter((col) => col.visible);
  if (adjustableColumns.length === 0) {
    return columns;
  }

  let lastVisibleColumn: DataGridColumnSettings | undefined;
  adjustableColumns.forEach((column) => {
    const columnWidth = column.width || getColumnDefaultWidth(column);
    // calculate the column scaling factor rounded to 2 decimal places
    const factor = Math.round((columnWidth / rowWidth) * 100) / 100;
    const adjustment = Math.floor(remainingWidth * factor);
    const newWidth = columnWidth + adjustment;

    const effectiveMinWidth = getColumnMinWidth(column);
    const effectiveMaxWidth = getColumnMaxWidth(column);

    //Respect column width constraints
    const finalWidth = Math.max(effectiveMinWidth, Math.min(newWidth, effectiveMaxWidth));
    column.width = finalWidth;

    // store for residual width update
    lastVisibleColumn = column;
  });

  // check for residual width due to rounding errors
  const adjustedRowWidth = calculateRowWidth(columns);
  if (totalWidth > adjustedRowWidth && lastVisibleColumn) {
    // assign residual space to last column
    const residualWidth = totalWidth - adjustedRowWidth;
    lastVisibleColumn.width! += residualWidth;
  }

  return columns;
}

/**
 * The new ES6 findIndex method is monstrously slower than the basic for loop and since this will be called each time
 * the row is rendered having a custom method would be preferable here.
 * @param {JsonData} rows - The selected or highlighted rows to search through.
 * @param {JsonDataItem} data - The data for a particular row
 * @param {DataGridColumnSettings} idField - The column used as the id field.
 * @returns {number} - The index our row data is found or -1 if not found.
 */
export function findRowIndex(rows: JsonData, data: JsonDataItem, idField?: DataGridColumnSettings) {
  const index = idField?.field ?? 'id';
  for (let i = rows.length; i-- !== 0; ) {
    if (rows[i][index] === data[index]) {
      return i;
    }
  }
  return -1;
}

const isTextFieldColumn = (column: Partial<DataGridColumnSettings>) => {
  return (
    (column.type === DataGridColumnType.Custom && column.customDataProperties?.fieldType === 'TEXTFIELD') ||
    column.type === DataGridColumnType.Text
  );
};

export const getColumnDefaultWidth = (column: Partial<DataGridColumnSettings>) => {
  if (isTextFieldColumn(column)) {
    return DEFAULT_MIN_TEXT_COLUMN_WIDTH_PX;
  }
  return DEFAULT_COLUMN_WIDTH_PX;
};

export const getColumnMaxWidth = (column: DataGridColumnSettings) => {
  if (column.maxWidth) {
    return column.maxWidth;
  }
  if (isTextFieldColumn(column)) {
    return DEFAULT_MAX_TEXT_COLUMN_WIDTH_PX;
  }
  return DEFAULT_MAX_COLUMN_WIDTH_PX;
};

export const getColumnMinWidth = (column: DataGridColumnSettings) => {
  if (column.minWidth) {
    return column.minWidth;
  }
  if (isTextFieldColumn(column)) {
    return DEFAULT_MIN_TEXT_COLUMN_WIDTH_PX;
  }
  return DEFAULT_MIN_COLUMN_WIDTH_PX;
};
